import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { JService } from "../../api-service/ApiService";

import Button from "../../design/Button";
import GHeader from "../../design/components/GHeader";
import LeftArrowIcon from "../../../assets/leftArrow.svg";
import CloseIcon from "../../../assets/closeIcon.svg";
import UserIcon from "../../../assets/user.svg";
import UsersIcon from "../../../assets/users.svg";
import Dialer from "../../../assets/dialer.svg";
import HangupIcon from "../../../assets/phone-x.svg";
import PlayAudio from "../../../assets/volume-max.svg";
import { plusIcon } from "../../../utility-functions/assetLoader";

import "@xyflow/react/dist/style.css";
import styles from "./ivr.module.css";
import {
  useNodesState,
  ReactFlow,
  Background,
  useEdgesState,
  ConnectionMode,
  Controls,
} from "@xyflow/react";
import { IconButton } from "@mui/material";
import KeypressActionNode from "./node-components/KeypressActionNode";
import IvrMenuNode from "./node-components/IvrMenuNode";
import PlayAudioActionNode from "./node-components/PlayAudioActionNode";
import IncomingCallTriggerNode from "./node-components/IncomingCallTriggerNode";
import {
  CustomInput,
  CustomSearchableDropdown,
  CustomSelect,
} from "../../design/components/customComponents";
import Audio from "../../survey/Audio";
import GModal from "../../design/components/GModal";
import HangupNode from "./node-components/HangupNode";
import EditOrderModal from "../EditOrderModal";
import { createPortal, flushSync } from "react-dom";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import colors from "../../design/colors";

// DONE: scroll to zoom to be changed to Ctrl+scroll or disabled
const maxResponseOptions = ["1 sec", "5 secs", "10 secs", "15 secs", "20 secs"];
const invalidResponseOptions = ["Repeat audio 1 time", "Repeat audio 2 times", "Repeat audio 3 times"];
const noValidInputOptions = ["End call with message", "Hang up immediately"]; //"Transfer to agent"

const nodeTypes = {
	incomingCallTriggerNode: IncomingCallTriggerNode,
	keypressActionNode: KeypressActionNode,
	ivrMenuNode: IvrMenuNode,
	playAudioActionNode: PlayAudioActionNode,
	hangupNode: HangupNode,
}
// used by ReactFlow
const NodeTypes = {
	incomingCallTriggerNode: "incomingCallTriggerNode",
	keypressActionNode: "keypressActionNode",
	ivrMenuNode: "ivrMenuNode",
	playAudioActionNode: "playAudioActionNode",
	hangupNode: "hangupNode",
}
// used for JSX
export const NodeActions = {
	toUser: "Connect to User",
	toTeam: "Connect to Team",
	playAudio: "Play Audio",
	ivr: "IVR Menu",
	hangup: "Hang Up",
	trigger: "Trigger"
}

const KeypressActionDropdownOptions = ["Connect to User", "Connect to Team", "Play Audio", "Hang Up", "IVR Menu"];
const Update_Component_Name_Wait_Time = 1000;
export default function Ivr() {
	const { state: { ivrId, virtualNumber, virtualNumberCountryCode, virtualNumberId, isNew, flowNamesInUse } } = useLocation();
	const navigate = useNavigate();

	// other data
	const [users, setUsers] = useState([]);
	const [teams, setTeams] = useState([]);
	const fetchRef = useRef({
		teams: [],
		users: []
	});

	// new states
	const [selectedNode, setSelectedNode] = useState({
		id: undefined,
		action: "",
		isKeypressAction: false,
	});
	const [nodesMap, setNodesMap] = useState({});
	const [connectToDropdown, setConnectToDropdown] = useState({});
	const [ivrKeypressActions, setIvrKeypressActions] = useState({});
	
	// react flow
	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	
	// other ui data
	const [flowSettings, setFlowSettings] = useState({});
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
	const [showRightColumn, setShowRightColumn] = useState(false);
	const [saveDisabled, setSaveDisabled] = useState(false);
	const leftColumnRef = useRef();
	const [showLoader, setShowLoader] = useState(false);
	const [showClickAwayAlert, setShowClickAwayAlert] = useState(false);
	const [showNodeFieldInvalidity, setShowNodeFieldInvalidity] = useState(false);

  // #initial api calls
  async function fetchUsersAndTeams() {
    try {
      const userRes = await JService.get("/api/v1/auth/retrieve-users/");
      if (userRes.success && userRes.data) {
        const fetchedUsers = userRes.data.map((u) => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`.trim(),
          email: u.email,
        }));
        fetchRef.current.users = fetchedUsers;
        setUsers(fetchedUsers);
      } else {
        console.error("Error fetching users:", userRes.message);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
    try {
      const teamRes = await JService.get("/api/v1/auth/teams/");
      if (teamRes.success && teamRes.data) {
        const fetchedTeams = teamRes.data.map((t) => ({
          id: t.id,
          name: t.name,
          users: t.users,
        }));
        fetchRef.current.teams = fetchedTeams;
        setTeams(fetchedTeams);
      } else {
        console.error("Error fetching teams:", teamRes.message);
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
    }
  }
  // get:ivr-flow/:id
  async function fetchIvrFlow() {
    try {
      const response = await JService.get(`/api/v1/core/ivr-flow/${ivrId}/`);
      if (!response?.success) throw new Error(response?.message);
      if (
        !response?.data ||
        typeof response.data !== "object" ||
        Object.keys(response.data).length === 0
      ) {
        throw new Error(
          response?.message || "Received an empty response for IVR flow"
        );
      }
	  	console.log("flowSettings response", response);
			setFlowSettings({
			  flowName: response.data.title,
			  maxResponseTime: `${response.data.response_time} secs`,
			  invalidResponse:
				response.data.repeat_question_option === 1
				  ? "Repeat audio 1 time"
				  : response.data.repeat_question_option === 2
				  ? "Repeat audio 2 times"
				  : "Hang up immediately",
			  noValidInput:
				response.data.end_action === 1
				  ? "End call with message"
				  : response.data.end_action === 2
				  ? "Hang up immediately"
				  : "Transfer to agent",
			  endCallAudio: null,
			  endCallAudioURL: response.data.end_action_recording_url || "",
			});
		}
		catch(error) {
			enqueueSnackbar(error?.message || "Error fetching IVR flow", { variant: "error" });
		}
	}
	async function fetchIvrComponents() {
		try {
		  const response = await JService.get(`/api/v1/core/ivr-components/?flow_id=${ivrId}`);
		  if(!response?.success) throw new Error(response?.message);
		  if(Object.keys(response.data).length) {
			repopulateNodes(response.data);
		  }
		}
		catch (e) {
		  enqueueSnackbar(e.message, { variant: "error" });
		}
	}
	useEffect(() => {
		// add incomingCallTriggerComponent to ui
		if(!nodes.length) {
			const newNode = {
				id: "1",
				action: NodeActions.trigger,
				position: {x: 100, y: 100},
				type: NodeTypes.incomingCallTriggerNode,
				data: {
					phoneNumber: virtualNumber,
					countryCode: virtualNumberCountryCode,
					invalid: false,
					name: "Trigger component",
				},
			};
			setConnectToDropdown(prev => ({
				...prev,
				[newNode.id]: {
					id: newNode.id,
					options: [
						{ id: "Play Audio", name: "Play Audio" },
						{ id: "IVR Menu", name: "IVR Menu" }
					],
					selected: null,
					next: undefined,
					previous: undefined,
				}
			}));
			setNodes([newNode]);
			setNodesMap(prev => ({
				...prev,
				[newNode.id]: newNode,
			}));
			// 	highlightNode("1", NodeTypes.incomingCallTriggerNode, {keypressAction: false, digit: undefined});
		}
		(async () => {
			await fetchUsersAndTeams();
			await fetchIvrComponents();
		})();
		fetchIvrFlow();		
	}, []);

	let headerTitle = useMemo(() => (nodes.find(n => n.selected) ?? {data:{}}).data.name || "No selection", [nodes]);

	// page-header & breadcrumbs
	if (flowSettings.name) bottomHeader.contentTitle = flowSettings.name; // set name for breadcrumbs
	const handleBackToVirtualNumbers = () => navigate(`/virtual-numbers/`);
	const breadcrumbs = useMemo(() => [
		<div key="bc-back" style={{ display: "flex", alignItems: "center", color: "#219848", paddingTop: "24px", paddingLeft: "32px", fontSize: "14px", cursor: "pointer" }} onClick={handleBackToVirtualNumbers}>
			<img src={LeftArrowIcon} alt="Back" style={{ width: 12, height: 12, marginRight: 8, filter: "invert(42%) sepia(83%) saturate(1915%) hue-rotate(77deg) brightness(101%) contrast(97%)" }} />
			Back to Virtual Numbers
			{/* Back to Number settings */}
		</div>
	], []);
	const topButtons = [
   <Button key="settings" variant="text" buttonText="Settings" buttonStyle={{ border: "1px solid #E4E4E7", borderRadius: "6px", color: "#3F3F46", fontFamily: "Inter", marginRight: "12px", fontWeight: 500, fontSize: "14px", lineHeight: "20px", padding: "6px 12px" }} onClick={() => setIsSettingsModalOpen(true)} />,
   <Button key="publish" hierarchy={saveDisabled ? "white" : "green"} variant="outlined" buttonText="Publish" buttonStyle={{ border: "1px solid #E4E4E7", textTransform: "none", fontFamily: "Inter", fontWeight: 500, fontSize: "14px", lineHeight: "20px", padding: "6px 12px" }} onClick={handlePublishFlow} disabled={false} styleOverride={{color: `${saveDisabled ? colors.nc_gray_900 : colors.ic_white}`}}/>
   // saveDisabled
]
	// useMemo(() =>
	// , [saveDisabled, nodes]);
	const bottomHeader = {
		contentTitle: flowSettings.flowName ?? "",
		buttons: topButtons,
		isCalendar: false,
		style: { paddingLeft: "32px", paddingTop: "0px", fontSize: "20px", paddingRight: "32px", marginTop: "16px" }
	};
	// connect ivr flow to vn 
	async function handlePublishFlow() {
		try {
			console.log("PublishFlow: allow publish?", !saveDisabled);
			flushSync(() => {
				checkNodesValidity();
			});
			if(saveDisabled) {
				enqueueSnackbar("Please complete all required fields before publishing the flow.", {variant: "error"});
				setShowNodeFieldInvalidity(true);
				return;
			}
			setShowNodeFieldInvalidity(false); // move under handleSaveFlow
			await handleSaveFlow();
			const payload = {
				ivr_flow: ivrId,
			};
			const url = `/api/v1/auth/virtual-numbers/calling/${virtualNumberId}/`;
			
			const response = await JService.patch(url, payload);
			if (!response.success) throw new Error(response?.message);
			enqueueSnackbar("IVR Flow published and assigned successfully", { variant: "success" });
			
		}
		catch (error) {
		  enqueueSnackbar(error.message, { variant: "error" });
		}
	}
	// helper for handleSaveFlow
	function getKeypressActionsPayload(id, res=[]) {
		if(nodesMap[id].type === NodeTypes.ivrMenuNode) {
			ivrKeypressActions[id].keypressActions.map((kp) => {
				if(kp.id) {
					const item = {
						digit: kp.digit,
						next_component_type: getType(kp.action),
						next_component_id: nodesMap[kp.id].apiId,
					};
					if(nodesMap[kp.id].type === NodeTypes.ivrMenuNode) {
						item.keypress_actions = getKeypressActionsPayload(kp.id);
					}
					else if(kp.action === NodeActions.playAudio) {
						item.keypress_actions = getKeypressActionsPayload(kp.id);
					}
					res.push(item);
				}
				return kp;
			});
		}
		else if(nodesMap[id].data.action === NodeActions.playAudio) {
			if(connectToDropdown[id].next) {
				const item = {
					digit: undefined,
					next_component_type: getType(connectToDropdown[id].selected),
					next_component_id: nodesMap[connectToDropdown[id].next].apiId,
				}
				if(connectToDropdown[id].selected === NodeActions.ivr) {
					item.keypress_actions = getKeypressActionsPayload(connectToDropdown[id].next);
				}
				res.push(item);
			}
		}				
		return res;
	}
	// onClick 'Save' in rightCol (saves keypressActions)
	const handleSaveFlow = async () => {
		try {
			const startComponentId = connectToDropdown[1].next;
			const startComponent = nodesMap[startComponentId];
			const payload = {
				start_component_id: (startComponent??{}).apiId,
			};
			payload.keypress_actions = getKeypressActionsPayload(startComponentId);
			const url = `/api/v1/core/ivr-component/create-key-press-actions/`;
			const response = await JService.post(url, payload);
			if(!response?.success) throw new Error(response?.message);
			setShowClickAwayAlert(false);
			// success message silenced
		}
		catch(error) {
			console.error(error);
			throw new Error(error.message);
		}
	}

	const nodesRef = useRef([]);
	const connectToRef = useRef({});
	const [validateNodes, setValidateNodes] = useState([]);
	useEffect(() => {
		nodesRef.current = nodes;
		connectToRef.current = connectToDropdown;

		setSaveDisabled(!isFlowValid());
	}, [nodes]);
	useEffect(() => {
		if(!validateNodes.length) return;

		validateNodes.forEach((id) => {
			isNodeInvalid(id);
		});
		setSaveDisabled(!isFlowValid());
		setValidateNodes([]);
	}, [validateNodes]);

	function repopulateNodes(data) {
		try {
			console.log("repopulateNodes", data);
			const ivrNodes = data.ivr_node ?? [];
			const audioNodes = data.recording ?? [];
			const userNodes = data.user ?? [];
			const teamNodes = data.team ?? [];
	
			const audioNodeIds = audioNodes.map(node => node.id);
			const ivrNodeIds = ivrNodes.map(node => node.id);
			const userNodeIds = userNodes.map(node => node.id);
			const teamNodeIds = teamNodes.map(node => node.id);
	
			// get start component	
			const allNodes = [...ivrNodes, ...audioNodes];
			allNodes.sort((a, b) => a.id - b.id);
			const keypressActionNodeIds = allNodes.filter(node => node.keypress_actions && node.keypress_actions.length).map(({keypress_actions}) => keypress_actions.map(kp => kp.next_component_id)).flat(1);
	
			const seenNodes = new Map();
			const _nodes = [];
			const _nodesMap = {"1": {
				...(nodesRef.current.at(0) ?? {})
			}};
			const _edges = [];
			const _ivrKeypressActions = {};
			const _connectToDropdown = {...connectToRef.current};
			let currentLevelPosition = {
				x: 100,
				y: 100,
				deltaX: 0,
				deltaY: 250,
			};
			let lastAddedNode = {
				id: '1',
			};
			let runningId = 2; // to generate local node ids
			let stateBeforeRecursion = {}; // for getKeypressActionNodesFrom

			allNodes.map((node, index) => {
				try {
					if(!seenNodes.has(node.id)) {
						seenNodes.set(node.id, node.id);
						const newNode = {
							id: `${runningId}`,
							apiId: node.id,
						};
		
						const action = getNodeAction(node.id);
						const isKeypressAction = keypressActionNodeIds.includes(node.id);
						currentLevelPosition.y += currentLevelPosition.deltaY;
						if(index === 0) {
							currentLevelPosition.y += 0.2 * currentLevelPosition.deltaY;
						}
						newNode.position = {
							x: currentLevelPosition.x,
							y: currentLevelPosition.y,
						};
						
						const keypressActionsPresent = Array.isArray(node.keypress_actions) && node.keypress_actions.length;
						
						if(action === NodeActions.ivr) {
							const url = node.recording_url ?? "";
							const fileName = url.split("/").at(-1) || "";
							newNode.type = NodeTypes.ivrMenuNode;
							newNode.data = {
								action,
								name: node.name,
								audio: {
									file:{
										name: fileName,
									},
									audioUrl: url,
								},
								keypressActions: [],
								action,
								invalid: false,
							};
							_ivrKeypressActions[newNode.id] = {
								isEntryNode: index === 0,
								keypressActions: [],
								audio: {
									file: undefined,
									audioUrl: url,
								}
							};
							if(fileName) {
								_ivrKeypressActions[newNode.id].audio.file = {
									name: fileName,
								}
							}
						}
						else if(action === NodeActions.playAudio) {
							const url = node.url ?? "";
							const fileName = url.split("/").at(-1) || "";
							newNode.type = NodeTypes.playAudioActionNode;
							newNode.data = {
								action,
								digit: "trigger",
								name: node.name,
								audio: {
									file: undefined,
									audioUrl: url,
								},
								action,
								invalid: false,
							};
							if(fileName) {
								newNode.data.audio.file = {
									name: fileName,
								}
							}
							if(index === 0) {
								// audio_node connected to the trigger_node can further omly connect to an ivr_node
								newNode.data.options = [
									{
										"id": "IVR Menu",
										"name": "IVR Menu"
									}
								]
							}
						}
					
						if(newNode.data) {
							// _states updated
							newNode.data.onDelete = e => {
								onDelete(e, newNode.id, lastAddedNode.id);
								setConnectToDropdown(prev => ({
									...prev,
									1: {
										...prev[1],
										selected: undefined,
									}
								}));
							}
							_nodes.push(newNode);
							_nodesMap[newNode.id] = {
								...newNode,
							};
							_nodesMap[lastAddedNode.id] = {
								..._nodesMap[lastAddedNode.id],
								selected: action,
							};
							_connectToDropdown[newNode.id] = {
								...newNode.data,
								id: newNode.id,
								previous: lastAddedNode.id,
								next: undefined,
								...newNode.data,
							};
							_connectToDropdown[lastAddedNode.id] = {
								..._connectToDropdown[lastAddedNode.id],
								next: newNode.id,
								selected: action,
							}
							_edges.push({ id: `e${lastAddedNode.id}-${newNode.id}`, source: lastAddedNode.id, target: newNode.id});

							runningId+=1;
							lastAddedNode = {
								action,
								id: newNode.id,
								isKeypressAction,
							}
							if(keypressActionsPresent) {
								console.log("repopulateNodes: getting kpas for", action, node.id);
								stateBeforeRecursion[node.id] = {
									...lastAddedNode,
								}
								getKeypressActionNodesFrom(node.id, newNode.position);
							}
						}
					}
				}
				catch(error) {
					console.error(error);
				}
			});
			setNodes(prev => [...prev, ..._nodes]);
			setEdges(_edges);
			setNodesMap(prev => ({
				...prev,
				..._nodesMap
			}));
			setConnectToDropdown(prev => ({...prev, ..._connectToDropdown}));
			setIvrKeypressActions(prev => ({...prev, ..._ivrKeypressActions}));
			
			function getKeypressActionNodesFrom(id, {x: parentX, y: parentY}) {
				try {
					const nodeWithKeypressActions = allNodes.find(node => node.id === id);
					if(nodeWithKeypressActions) {
						const keypressActions = nodeWithKeypressActions.keypress_actions;
						const xOffset =  keypressActions.length > 1 ? parentX - 200 : 0; // lastAddedNode.action === NodeActions.ivr ?
						const halfLength = Math.floor(keypressActions.length/2);
						const getx = (digit) => digit ? (xOffset) + (Number(digit) - halfLength) * 400 : parentX;
						let lastNode = {
							x: parentX,
							y: parentY,
							id,
						};
						let localParentNode = _nodes.find(node => node.apiId === id) ?? {};
						let newKeypressActionData = {};
						keypressActions.map(({digit, next_component_id, next_component_type, is_hangup, component}) => {
							try {
								if(is_hangup) {
									if(seenNodes.has(`${digit}_${id}`)) return;
								}
								else if(seenNodes.has(next_component_id)) return;
		
								const newNode = {
									id: `${runningId}`,
									apiId: is_hangup ? undefined : component.id,
								};
								let keypressActionsPresent;
								const action = is_hangup ? NodeActions.hangup : getNodeAction(component.id);
		
								newNode.position = {
									x: lastNode.x,
									y: lastNode.y,
								};
								if(xOffset && digit) newNode.position.x = getx(digit);
								if (lastNode.id === id) {
									newNode.position.y += currentLevelPosition.deltaY;
								}
								
								if(action === NodeActions.ivr) {
									newNode.type = NodeTypes.ivrMenuNode;
								
									//ivrKeypressActions
									const url = component.recording_url ?? "";
									const fileName = url.split("/").at(-1) || "";
		
									keypressActionsPresent = Array.isArray(component.keypress_actions) && component.keypress_actions.length;
		
									newNode.data = {
										name: component.name,
										audio: {
											file: {
												name: fileName,
											},
											audioUrl: url,
										},
										keypressActions: [],
										action,
										digit,
									};
									newKeypressActionData = {
										action,
										digit,
										id: newNode.id,
									};
									_ivrKeypressActions[newNode.id] = {
										isEntryNode: false,
										keypressActions: [],
										audio: {
											file: {
												name: fileName,
											},
											audioUrl: url,
										}
									};
									if(fileName) {
										_ivrKeypressActions[newNode.id].audio.file = {
											name: fileName,
										}
									}
								}
								else if(action === NodeActions.playAudio) {
									newNode.type = NodeTypes.keypressActionNode;
		
									// nodesMap
									const url = component.url;
									const fileName = url.split("/").at(-1) ?? "";
		
									keypressActionsPresent = Array.isArray(component.keypress_actions) && component.keypress_actions.length;
		
									newNode.data = {
										name: component.name,
										audio: {
											file: undefined,
											audioUrl: url,
										},
										action,
										digit,
									};
									newKeypressActionData = {
										digit,
										label: "",
										action,
										id: newNode.id,
										options: ["Connect to User", "Connect to Team", "IVR Menu", NodeActions.hangup].map(i => ({id: i, name: i})),
										selected: undefined,
									}
									if(fileName) {
										newNode.data.audio.file = {
											name: fileName
										}
									}
								}
								else if(action === NodeActions.toUser) {
									newNode.type = NodeTypes.keypressActionNode;
									const selected = component.user;
									const selectedItem = fetchRef.current.users.find(user => user.id === selected);
									const selectedName = selectedItem.name;
									newNode.data = {
										digit,
										name: component.name,
										label: next_component_type,
										options: fetchRef.current.users,
										selected,
										selectedName,
										action,
									}
									newKeypressActionData = {
										label: newNode.data.label,
										options: newNode.data.options,
										action,
										digit,
										id: newNode.id,
										selected,
										selectedName,
									};
								}
								else if(action === NodeActions.toTeam) {
									newNode.type = NodeTypes.keypressActionNode;
									const {call_distribution: callDistribution, team: selected, routing_order: routingOrder, team_details} = component;
									newNode.data = {
										digit,
										name: component.name,
										label: next_component_type,
										options: fetchRef.current.teams,
										selected,
										selectedName: team_details.name,
										callDistribution: String(callDistribution),
										routingOrder,
										action,
									}
									newKeypressActionData = {
										label: newNode.data.label,
										options: newNode.data.options,
										action,
										digit,
										id: newNode.id,
										callDistribution: newNode.data.callDistribution,
										routingOrder,
										callDistributionOptions: [
											{ id: "5", name: "Simultaneous ringing" },
											{ id: "1", name: "Round Robin" },
											{ id: "2", name: "Fixed Order" },
											{ id: "3", name: "Longest Idle" },
											{ id: "4", name: "Sticky Agent" }
										],
										selected,
										selectedName: newNode.data.selectedName,
									};
								}
								else if(action === NodeActions.hangup) {
									newNode.type = NodeTypes.hangupNode;
									newNode.position.y -= 8;
									newNode.data = {
										digit,
										name: "Hangup component",
										action,
									};
									newKeypressActionData = {
										action,
										digit,
										id: newNode.id,
									}
		
								}
		
								if(newNode.data) {
									newNode.data.onDelete = e => onDelete(e, newNode.id, localParentNode.id);
									newNode.data.invalid = false;
									_nodes.push(newNode);
									if(is_hangup) seenNodes.set(`${digit}_${id}`, `${digit}_${id}`);
									else seenNodes.set(next_component_id, next_component_id);
									const newEdge = { id: `e${localParentNode.id}-${newNode.id}`, source: localParentNode.id, target: newNode.id};
									const newKeypressAction = createEmptyKeypressAction(digit??'', newKeypressActionData);
									
									// state updates
									if(digit && !isNaN(Number(digit))) { // check !!digit based on whether to allow/disallow 0
										// connected to ivr node
										newEdge.sourceHandle = `handle-ivr-${digit}`;
										
										localParentNode.data.keypressActions = [
											...localParentNode.data.keypressActions,
											newKeypressAction
										];
										_ivrKeypressActions[localParentNode.id] = {
											..._ivrKeypressActions[localParentNode.id],
											keypressActions: [...((_ivrKeypressActions[localParentNode.id] ?? {}).keypressActions ?? []), newKeypressAction]
										};
									}
									_nodesMap[newNode.id] = {
										...newNode,
									};
									_nodesMap[lastAddedNode.id] = {
										..._nodesMap[lastAddedNode.id],
										selected: action,
									};
									_connectToDropdown[newNode.id] = {
										...newNode.data,
										id: newNode.id,
										previous: lastAddedNode.id,
										next: undefined,
										...newKeypressAction,
										apiId: next_component_id,
									};
									_connectToDropdown[lastAddedNode.id] = {
										..._connectToDropdown[lastAddedNode.id],
										next: newNode.id,
										selected: action,
									}

									_edges.push(newEdge);
									runningId+=1;
									lastNode = {
										...lastNode,
										action,
										...newNode.position,
										id: newNode.apiId,
									};
									if(keypressActionsPresent) {
										stateBeforeRecursion[id] = {...lastAddedNode, isConsumed: false};
										lastAddedNode = {
											action,
											id: newNode.id,
											isKeypressAction: true,
										}
										getKeypressActionNodesFrom(component.id, newNode.position);
										stateBeforeRecursion[id].isConsumed = true;
										lastAddedNode = {
											...stateBeforeRecursion[id],
										}
									}
								}
							}
							catch(error) {
								console.error(error);
							}
						});
					}
				}
				catch(error) {
					console.error(error);
				}
			}
			function getNodeAction(id) { // id like apiId
				if(ivrNodeIds.includes(id)) return NodeActions.ivr;
				else if(userNodeIds.includes(id)) return NodeActions.toUser;
				else if(teamNodeIds.includes(id)) return NodeActions.toTeam;
				else if(audioNodeIds.includes(id)) return NodeActions.playAudio	;
			}
		}
		catch(error) {
			console.error(error);
		}
	}
	
	// misc helpers
	function getNewNodeId(currentLength) {
		let newId = String(Math.max(...nodesRef.current.map(n => Number(n.id))) + 1);//(nodesRef.current.length + 1).toString();
		return currentLength ? (currentLength+1).toString() : newId;
	}
	const createEmptyKeypressAction = (digit, data={}) => ({
		digit: digit.toString(),
		isRendered: Boolean(digit),
		action: "",
		options: [],
		selected: undefined,
		label: "",
		id: undefined,
		...data,
	});
	const handleHideRightCloumn = () => {
		setSelectedNode({
			id: undefined,
			action: "",
			isKeypressAction: false,
		});
		selectNode(undefined);
		//setShowRightColumn set in useEffect
	}
	useEffect(() => setShowRightColumn(Boolean(selectedNode.id)), [selectedNode]);

	// connect to for Trigger
	const handleConnectToTrigger = (action, parentNodeId = "1") => {
		let length;
		// check and remove already connected node
		if(connectToDropdown[parentNodeId].selected) {
			const deleteNodeId = connectToDropdown[parentNodeId].next;
			length = deleteNode(deleteNodeId, parentNodeId);
		}

		const newNode = {
			id: getNewNodeId(length),
			position: {x: 100, y: 400},
		};
		let dropdownOptions;

		if(action === NodeActions.ivr) {
			const keypressActions = [
				createEmptyKeypressAction(1),
				createEmptyKeypressAction(2),
			];
			newNode.type = NodeTypes.ivrMenuNode;
			newNode.data = {
				action,
				name: "IVR component",
				keypressActions,
				onDelete: (e) => {
					e.stopPropagation();
					deleteNode(newNode.id, parentNodeId, true);
				},
			};
			setIvrKeypressActions(prev => ({
				...prev,
				[newNode.id]: {
					isEntryNode: true,
					keypressActions,
					audio: {
						file: undefined,
						audioUrl: undefined,
					}
				}
			}));
		}
		else if (action === NodeActions.playAudio) {
			newNode.type = NodeTypes.playAudioActionNode;
			newNode.data = {
				name: "Recording component",
				digit: "trigger",
				action,
				audio: {
					file: undefined,
					audioUrl: undefined,
				},
				componentType: "recording",
				onDelete: (e) => {
					e.stopPropagation();
					// handleNodeDelete(newNode.id);
					deleteNode(newNode.id, parentNodeId, true);
				},
				children: [],
			};
			dropdownOptions = [
				{ id: "IVR Menu", name: "IVR Menu" },
			]
		}

		if(newNode.data) {
			newNode.data.onDelete = e => onDelete(e, newNode.id, parentNodeId)
			newNode.apiId = undefined;
			newNode.selected = false;
			newNode.data.invalid = true;
			
			setShowClickAwayAlert(true);
			setNodes(prev => [...prev, newNode]);
			setNodesMap(prev => ({
				...prev,
				[newNode.id]: {...newNode},
			}));
			setConnectToDropdown(prev => ({
				...prev,
				[parentNodeId]: {
					...prev[parentNodeId],
					selected: action,
					next: newNode.id,
				},
				[newNode.id]: {
					id: newNode.id,
					options: dropdownOptions,
					selected: null,
					next: undefined,
					previous: parentNodeId,
					action,
				}
			}));
			setEdges(prev => [...prev, { id: `e${parentNodeId}-${newNode.id}`, source: parentNodeId, target: newNode.id}]);
			createNodeAPI(newNode.id, action, true);
		}
	}
	// connect to for audio nodes
	const handleConnectToForAudioNode = (parentNodeId, action, isKeypressAction=false) => {
		let length;
		if(connectToDropdown[parentNodeId].selected) {
			// remove already connected node
			const deleteNodeId = connectToDropdown[parentNodeId].next;
			length = deleteNode(deleteNodeId, parentNodeId);
		}
		
		let newNode = {
			id: getNewNodeId(length),
			position: {
				x: nodesMap[parentNodeId].position.x,
				y: nodesMap[parentNodeId].position.y + 200,
			},
		};
		let dropdownOptions, label, extraOptions={};
		if(action === NodeActions.toUser) {
			newNode.type = NodeTypes.keypressActionNode;
			dropdownOptions = users;
			label = "user";
			newNode.data = {
				name: "User component",
				action,
				label,
				options: dropdownOptions,
				digit: "",
				nonKpaDigit: newNode.id,
				user: undefined, // user.name
				onDelete: (e) => {
					e.stopPropagation();
					deleteNode(newNode.id, parentNodeId);
				},
				invalid: true,
			};
		}
		else if(action === NodeActions.toTeam) {
			newNode.type = NodeTypes.keypressActionNode;
			dropdownOptions = teams;
			label = "team";
			extraOptions = {callDistribution: "1", routingOrder: [], digit: "", callDistributionOptions: [
				{ id: "5", name: "Simultaneous ringing" },
				{ id: "1", name: "Round Robin" },
				{ id: "2", name: "Fixed Order" },
				{ id: "3", name: "Longest Idle" },
				{ id: "4", name: "Sticky Agent" }
			]};
			newNode.data = {
				name: "Team component",
				label,
				options: dropdownOptions,
				action,
				callDistribution: extraOptions.callDistribution,
				digit: "",
				team: undefined,
				onDelete: (e) => {
					e.stopPropagation();
					deleteNode(newNode.id, parentNodeId);
				},
				invalid: true,
			};
		}
		else if(action === NodeActions.ivr) {
			newNode.type = NodeTypes.ivrMenuNode;
			const keypressActions = [
				createEmptyKeypressAction(1),
				createEmptyKeypressAction(2),
			];
			newNode.data = {
				action,
				name: "IVR component",
				keypressActions,
				onDelete: (e) => {
					e.stopPropagation();
					deleteNode(newNode.id, parentNodeId);
				},
				invalid: true,
			};
			
			setIvrKeypressActions(prev => ({
				...prev,
				[newNode.id]: {
					isEntryNode: false,
					keypressActions,
					audio: {
						file: undefined,
						audioUrl: undefined,
					}
				}
			}));
		}
		else if (action === NodeActions.hangup) {
			newNode.type = NodeTypes.hangupNode;
			newNode.data = {
				name: "Hangup component",
				action,
				digit: undefined,
				onDelete: (e)=> {
					e.stopPropagation();
					deleteNode(newNode.id, parentNodeId);
				},
				invalid: false,
			}
		}

		if(newNode.data) {
			newNode.apiId = undefined;
			newNode.data.onDelete = e => onDelete(e, newNode.id, parentNodeId)
			newNode.data.invalid = action !== NodeActions.hangup;
			flushSync(() => {
				setShowClickAwayAlert(true);
				setNodes(prev => [...prev, newNode]);
				setNodesMap(prev => ({
					...prev,
					[newNode.id]: newNode,
					[parentNodeId]: {
						...prev[parentNodeId],
						selected: action,
						next: newNode.id,
					},
				}));
				setConnectToDropdown(prev => ({
					...prev,
					[parentNodeId]: {
						...prev[parentNodeId],
						selected: action,
						next: newNode.id,
					},
					[newNode.id]: {
						id: newNode.id,
						options: dropdownOptions,
						selected: null,
						next: undefined,
						previous: parentNodeId,
						
						...extraOptions,
						action,
					}
				}));
				setEdges(prev => [...prev, { id: `e${parentNodeId}-${newNode.id}`, source: parentNodeId, target: newNode.id}]);
			});
			createNodeAPI(newNode.id, action);
			checkNodesValidity();
		}
	}
	// on new EMPTY keypress action field created in rightCol
	const handleAddNewKeypressAction = (ivrNodeId) => {
		// gets largest present keypress digit or 0 (new digit added is maxDigit + 1) // use set or arr to fill missing keypresses first instead
		const minimumAllowedDigit = 0; // use -1 to allow addition of '0' 
		const maxDigit = Math.max(...ivrKeypressActions[ivrNodeId].keypressActions.map(kp => Number(kp.digit)), minimumAllowedDigit);
		if(maxDigit === 9) {
			console.warn("Cannot add anymore keypress actions");
			return;
		}
		const newKeypressAction = createEmptyKeypressAction(maxDigit + 1);
		setShowClickAwayAlert(true);
		setIvrKeypressActions(prev => ({
			...prev,
			[ivrNodeId]: {
				...prev[ivrNodeId],
				keypressActions: [...prev[ivrNodeId].keypressActions, newKeypressAction],
			}
		}));
		
		setNodesMap(prev => ({
			...prev,
			[ivrNodeId]: {
				...prev[ivrNodeId],
				data: {
					...prev[ivrNodeId].data,
					keypressActions: [...prev[ivrNodeId].data.keypressActions, newKeypressAction],
				}
			}
		}));
		setNodes(prev => [...prev.map(p => p.id === ivrNodeId ? ({
			...p,
			data: {
				...p.data,
				keypressActions: [...p.data.keypressActions, newKeypressAction],
				invalid: true,
			}
		}) : p)]);
		checkNodesValidity();
	}
	// selecting ACTION from dropdown of already created IVR node's KEYPRESS ACTION
	const handleSelectKeypressActionInDropdown = (ivrNodeId, action, digit) => {
		const halfLength = Math.floor(ivrKeypressActions[ivrNodeId].keypressActions.length/2);
		const st = nodesMap[ivrNodeId].position.x - 200;
		const getx = (digit) => (st) + (Number(digit) - halfLength) * 400
		let newNode = {
			id: getNewNodeId(),
			// y from ivrNode, x from ivrNode + already set kpas
			position: {
				y: nodesMap[ivrNodeId].position.y + 350,
				x: getx(digit),
			},
			type: NodeTypes.keypressActionNode,
		};
		let dropdownOptions, label, extraOptions={};
		if(action === NodeActions.toUser) {
			label = "user";
			dropdownOptions = users;
			newNode.data = {
				action,
				digit,
				label,
				options: dropdownOptions,
				name: "User component",
				invalid: true,
				user: undefined,
			};
		}
		else if(action === NodeActions.toTeam) {
			label = "team";
			dropdownOptions = teams;
			extraOptions = {
				callDistribution: "1",
				routingOrder: [],
				digit,
				callDistributionOptions: [
					{ id: "5", name: "Simultaneous ringing" },
					{ id: "1", name: "Round Robin" },
					{ id: "2", name: "Fixed Order" },
					{ id: "3", name: "Longest Idle" },
					{ id: "4", name: "Sticky Agent" }
				]
			}
			newNode.data = {
				action,
				digit,
				team: undefined,
				name: "Team component",
				label,
				callDistribution: extraOptions.callDistribution,
				invalid: true,
			};
		}
		else if(action === NodeActions.playAudio) {
			dropdownOptions = ["Connect to User", "Connect to Team", "IVR Menu", NodeActions.hangup].map(i => ({id: i, name: i}));
			newNode.data = {
				name: "Recording component",
				action,
				digit,
				user: undefined,
				options: dropdownOptions,
				selected: undefined,
				audio: {
					file: undefined,
					audioUrl: undefined,
				},
				children: [],
				invalid: true,
			};
			// newNode.type = NodeTypes.playAudioActionNode;
		}
		else if(action === NodeActions.hangup) {
			newNode.type = NodeTypes.hangupNode;
			newNode.position.y -= 8;
			newNode.data = {
				name: "Hangup component",
				action,
				digit,
				invalid: false,
			};
		}
		else if(action === NodeActions.ivr) {
			// update connectTo dropdown by adding newNode and updating parent.next
			newNode.type = NodeTypes.ivrMenuNode;
			const keypressActions = [
				createEmptyKeypressAction(1),
				createEmptyKeypressAction(2),
			];
			newNode.data = {
				action,
				name: "IVR component",
				keypressActions,
				digit,
				invalid: true,
			};
			
			setIvrKeypressActions(prev => ({
				...prev,
				[newNode.id]: {
					isEntryNode: false,
					keypressActions,
					audio: {
						file: undefined,
						audioUrl: undefined,
					}
				}
			}));
		}
		if(newNode.data) {
			setShowClickAwayAlert(true);
			newNode.data.onDelete = e => onDelete(e, newNode.id, ivrNodeId)
			newNode.apiId = undefined;
			const updatePositionList = ivrKeypressActions[ivrNodeId].keypressActions.filter(kp => kp.id);
			flushSync(() => {
				// set ivr as invalid if added node is not hangup
				setIvrKeypressActions(prev => ({
					...prev,
					[ivrNodeId]: {
						...prev[ivrNodeId],
						keypressActions: [...prev[ivrNodeId].keypressActions.map(kp => kp.digit === digit ? ({
							...kp,
							digit,
							label,
							action,
							selected: undefined,
							options: dropdownOptions,
							id: newNode.id,
							...extraOptions,
						}) : kp)]
					}
				}));
				
				setConnectToDropdown(prev => ({
					...prev,
					[ivrNodeId]: {
						...prev[ivrNodeId],
						selected: action,
						next: newNode.id,
					},
					[newNode.id]: {
						id: newNode.id,
						options: dropdownOptions,
						selected: null,
						next: undefined,
						previous: ivrNodeId,
						// selectedName: "",
	
						...extraOptions,
						action,
					}
				}));
				
				setNodes(prev => [...(prev.map(node => node.id === ivrNodeId ? (
					{
						...node,
						data: {
							...node.data,
							keypressActions: [...(node.data.keypressActions.map(kp => kp.digit === digit ? (
								{
									...kp,
									action,
									id: newNode.id,
								}
							) : kp))],
							invalid: newNode.action === NodeActions.hangup ? node.data.invalid : newNode.data.invalid,
						}
					}
				) : updatePositionList.find(item => item.id === node.id) ? (
					{
						...node,
						position: {
							y: nodesMap[ivrNodeId].position.y + 350,
							x: getx(node.data.digit),
						}
					}
				) : node)), newNode]);
	
				setNodesMap(prev => ({
					...prev,
					[ivrNodeId]: {
						...prev[ivrNodeId],
						data: {
							...prev[ivrNodeId].data,
							invalid: newNode.action === NodeActions.hangup ? prev[ivrNodeId].data.invalid : newNode.data.invalid,
							keypressActions: [...prev[ivrNodeId].data.keypressActions.map(kp => kp.digit === digit ? (
								{
									...kp,
									action,
								}
							) : kp)],
						}
					},
					[newNode.id]: newNode,
				}));
				setEdges(prev => [...prev, { id: `e${ivrNodeId}-${newNode.id}`, source: ivrNodeId, target: newNode.id, sourceHandle: `handle-ivr-${digit}`}]);
			});
			createNodeAPI(newNode.id, action);
			checkNodesValidity();
		}
	}


	// when selecting a user, a team etc. for a keypressaction in rightCol (action for kpa Node is set at this point), and toUser and toTeam nodes connected to playAudio.
	const handleUpdateCallDistribution = async ({value, digit, updatedNodeId, isCallDistribution}) => {
		const data = {
			callDistribution: isCallDistribution ? value : connectToDropdown[updatedNodeId].callDistribution,
			routingOrder: isCallDistribution ? connectToDropdown[updatedNodeId].routingOrder : value,
		}
		const parentNodeId = connectToDropdown[updatedNodeId].previous;
		const isParentNodeIvr = nodesMap[parentNodeId].type === NodeTypes.ivrMenuNode;
		if(isParentNodeIvr) {
			setIvrKeypressActions(prev => ({
				...prev,
				[parentNodeId]: {
					...prev[parentNodeId],
					keypressActions: [...prev[parentNodeId].keypressActions.map(kp => kp.digit === digit ? ({
						...kp,
						...data,
					}) : kp)]
				}
			}));
		}
		setNodes(prev => [...prev.map(node => isParentNodeIvr && node.id === parentNodeId ? (
			// new ivrNode
			{
				...node,
				data: {
					...node.data,
					keypressActions: [...node.data.keypressActions.map(kp => kp.digit === digit ? ({
						...kp,
						...data,
					}) : kp)]
				},
			}
		) : node.id === updatedNodeId ? (
			// node connected to digit of ivr node
			{
				...node,
				data: {
					...node.data,
					...data,
				},
			}
		) : node)]);
		setConnectToDropdown(prev => ({
			...prev,
			[updatedNodeId]: {
				...prev[updatedNodeId],
				...data,
			}
		}));
		updateNodeAPI(updatedNodeId, {
			routing_order: data.routingOrder,
			call_distribution: Number(data.callDistribution)
		});
		
		checkNodesValidity();
	}
	const handleUpdateNode = async ({value, digit, action, parentNodeId, updatedNodeId}) => {
		let invalid = true;
		const payload = {};
		if(digit) {
			if(!updatedNodeId) {
				const updatedNode = ivrKeypressActions[parentNodeId].keypressActions.find(kp => kp.digit === digit);
				updatedNodeId = updatedNode.id;
			}
			else if (!parentNodeId) {
				parentNodeId = connectToDropdown[updatedNodeId].previous;
			}
		}
		// const extraNodesToValidate = [];
		// user gets added or changed
		if(action === NodeActions.toUser) {
			const user = users.find((u) => u.id === value);
			const userName = user.name;
			payload.user = value;
			invalid = false;
			if(digit) {
				setIvrKeypressActions(prev => ({
					...prev,
					[parentNodeId]: {
						...prev[parentNodeId],
						keypressActions: [...prev[parentNodeId].keypressActions.map(kp => kp.digit === digit ? ({
							...kp,
							digit,
							selected: value,
						}) : kp)]
					}
				}));
			}
			setNodes(prev => [...prev.map(node => node.id === parentNodeId ? (
				// new ivrNode
				{
					...node,
					data: {
						...node.data,
						keypressActions: [...node.data.keypressActions.map(kp => kp.digit === digit ? ({
							...kp,
							selected: value,
							selectedName: userName,
						}) : kp)]
					},
				}
			) : node.id === updatedNodeId ? (
				// node connected to digit of ivr node
				{
					...node,
					data: {
						...node.data,
						selectedName: userName,
						invalid,
					},
				}
			) : node)]);
			setConnectToDropdown(prev => ({
				...prev,
				[updatedNodeId]: {
					...prev[updatedNodeId],
					selected: value,
				},
				// ...newStateForParentAudioNode,
			}));			
		}
		else if(action === NodeActions.toTeam) {
			const team = teams.find((u) => u.id === value);
			const teamName = team.name;
			payload.team = value;
			payload.call_distribution = connectToDropdown[updatedNodeId].callDistribution;
			const selectedTeam = teams.find(team => team.id === value);
			const usersInSelectedTeam = selectedTeam.users;
			payload.routing_order = usersInSelectedTeam;

			if(connectToDropdown[updatedNodeId].callDistribution && connectToDropdown[updatedNodeId].routingOrder) {
				invalid = false;
			}
			else invalid = true;

			if(digit) {
				setIvrKeypressActions(prev => ({
					...prev,
					[parentNodeId]: {
						...prev[parentNodeId],
						keypressActions: [...prev[parentNodeId].keypressActions.map(kp => kp.digit === digit ? ({
							...kp,
							digit,
							selected: value,
						}) : kp)]
					}
				}));
			}
			setNodes(prev => [...prev.map(node => node.id === parentNodeId ? (
				// new ivrNode
				{
					...node,
					data: {
						...node.data,
						keypressActions: [...node.data.keypressActions.map(kp => kp.digit === digit ? ({
							...kp,
							selected: value,
							selectedName: teamName,
						}) : kp)]
					},
				}
			) : node.id === updatedNodeId ? (
				// node connected to digit of ivr node
				{
					...node,
					data: {
						...node.data,
						selectedName: teamName,
						invalid,
					},
				}
			) : node)]);
			setConnectToDropdown(prev => ({
				...prev,
				[updatedNodeId]: {
					...prev[updatedNodeId],
					selected: value,
					callDistribution: payload.call_distribution,
					routingOrder: payload.routing_order,
				}
			}));
		}
		//doesnt reach below
		else if(action === NodeActions.playAudio) {
			console.log("handleUpdate: unknown state reached");
		}
		updateNodeAPI(updatedNodeId, payload);
		checkNodesValidity();
	}
	// ivr node audio file upload
	const handleUpdateIvrAudio = (id, data) => {
		let isApiCallUpdate = false;
		if(!data) {
			isApiCallUpdate = true;
			data = {
				file: undefined,
				audioUrl: undefined,
			}
		}
		else if(data.file) isApiCallUpdate = true;

		if(data.file && data.file.size/(1024 * 1024) > 10) {
			enqueueSnackbar("Audio file exceeds the 10MB limit. Please upload a smaller file.", {variant: "error"});
			// remove file from data
			data.file = undefined;
			data.audioUrl = undefined;
			isApiCallUpdate = false;
		}

		setIvrKeypressActions(prev => ({
			...prev,
			[id]: {
				...prev[id],
				audio: {
					...prev[id].audio,
					...data,
				}
			}
		}));
		setNodes(prev => [...prev.map(node => node.id === id ? (
			{
				...node,
				data: {
					...node.data,
					audio: {
						...node.data.audio,
						...data,
					}
				}
			}
		) : node)]);
		if(isApiCallUpdate) {
			updateNodeAPI(id, {file: data.file, isAudio: true, payloadField: data.file ? undefined : "recording_url"});
		}

		checkNodesValidity();
	}
	const handleUpdateAudioNode = (id, data) => {
		let isUpdatingFile = true;
		if(!data) {
			data = {
				file: undefined,
				audioUrl: undefined,
			}
		}
		else if(data.audioUrl) isUpdatingFile = false;
		// if file exists check file size > 10MB
		if(data.file && data.file.size/(1024 * 1024) > 10) {
			enqueueSnackbar("Audio file exceeds the 10MB limit. Please upload a smaller file.", {variant: "error"});
			// remove file from data
			data.file = undefined;
			data.audioUrl = undefined;
			isUpdatingFile = false;
		}
		setNodesMap(prev => ({
			...prev,
			[id]: {
				...prev[id],
				data: {
					...prev[id].data,
					audio: {
						...prev[id].data.audio,
						...data,
					}
				}
			}
		}));
		setNodes(prev => [...prev.map(node => node.id === id ? (
			{
				...node,
				data: {
					...node.data,
					audio: {
						...node.data.audio,
						...data,
					}
				}
			}
		) : node)]);
		if(isUpdatingFile) {
			updateNodeAPI(id, {file: data.file, isAudio: true, payloadField: data.file ? undefined : "url"});
		}
		checkNodesValidity();
	}



	// update node name
	const updateNameRef = useRef({
		apiId: undefined,
		// id: undefined,
		cancelId: undefined,
		previousSavedValue: "",
		msTime: Update_Component_Name_Wait_Time,
	});
	// path: ivr-component/:id/
	async function updateComponentNameApi(apiId, payload) {
		try {
			console.log("updateComponentNameApi", apiId, payload, updateNameRef.current);
			const url = `/api/v1/core/ivr-component/${apiId}/`;
			await JService.patch(url, payload);
			updateNameRef.current.previousSavedValue = payload.name;
			// success message silenced
			try {
				const node = nodesRef.current.find(node => node.apiId === apiId);
				if(node) {
					applyNameToComponent(node.id, updateNameRef.current.previousSavedValue);
				}
			}
			catch(error) {
				console.error(error);
			}
		}
		catch(error) {
			enqueueSnackbar(error.message, { variant: "error" });
		}
		finally {
			updateNameRef.current.cancelId = undefined;
		}
	}
	// editing node name in rightCol
	const handleUpdateComponentName = (id, value, prevValue) => {
		const node = nodesRef.current.find(n => n.id === id);
		if(!node || !nodesMap[id] || !nodesMap[id].data) {
			// reached bad state
			console.error("handleUpdateComponentName: unknown state reached with id:", id);
		}
		const apiId = node.apiId;
		let previousSavedValue = nodesMap[id].data.name;
		setNodesMap(prev => ({
			...prev,
			[id]: {
				...prev[id],
				data: {
					...prev[id].data,
					name: value,
				}
			}
		}));	

		if(value) {
			if(apiId === updateNameRef.current.apiId) {
				// same component name being edited
				if(updateNameRef.current.previousSavedValue !== value) {
					// cancel previous api call
					if(updateNameRef.current.cancelId) clearTimeout(updateNameRef.current.cancelId);
					// schedule api call
					if(previousSavedValue !== updateNameRef.current.previousSavedValue) updateNameRef.current.cancelId = setTimeout(() => updateComponentNameApi(apiId, {name: value}), updateNameRef.current.msTime);
				}
				// else value is same, ignore. allow previously scheduled api call, if any, to get executed
			}
			else {
				// another component name being edited, allow prev req if any to go through.
				if(value !== previousSavedValue) updateNameRef.current.cancelId = setTimeout(() => updateComponentNameApi(apiId, {name: value}), updateNameRef.current.msTime);
			}
			updateNameRef.current.apiId = apiId;
		}
	}
	const applyNameToComponent = (id, name) => {
		setNodes(prev => prev.map(node => node.id === id ? ({...node, data: {...node.data, name,}}) : node));
		setNodesMap(prev => ({
			...prev,
			[id]: {
				...prev[id],
				data: {
					...prev[id].data,
					name,
				}
			}
		}));
	}



	// DONE: api calls for crud on nodes
	// post: create-ivr-component/
	async function createNodeAPI(id, type, isStartComponent=undefined) {
		try {
			let apiId;
			setShowLoader(true);
			// api call if not hangup node
			if(type !== NodeActions.hangup) {
				const payload = {
					flow_id: ivrId,
					component_type: getType(type),
				};
				if(isStartComponent) payload.is_start_component = true;
				const url = `/api/v1/core/create-ivr-component/`;
				const response = await JService.post(url, payload);
				if(!(response.data??{}).id) throw new Error(response?.message || "Failed to create node");
				// success message silenced
				apiId = response.data.id;
				applyNameToComponent(id, response.data.name);
				
			}
			setShowClickAwayAlert(true);
			// update states
			setNodes(prev => prev.map(node => {
				if(node && node.id === id) node.apiId = apiId;
				return node;
			}));
			setNodesMap(prev => ({
				...prev,
				[id]: {
					...prev[id],
					apiId,
				}
			}));
			setConnectToDropdown(prev => ({
				...prev,
				[id]: {
					...prev[id],
					apiId,
				}
			}));
		}
		catch(error) {
			enqueueSnackbar(error.message, { variant: "error" });
		}
		finally {
			setShowLoader(false)
		}
	}
	// patch: ivr-component/:id/
	async function updateNodeAPI(id, data={}) {
		try {
			setShowLoader(true);
			let node = nodes.find(node => node.id === id);
			if(!node) throw new Error("Component not found");
			let apiId = node.apiId;
			if(!apiId) throw new Error("Component is missing");
			let payload;
			const formData = new FormData();
			if(data?.isAudio && !data.payloadField) {
				const newFilename = data.file.name.replace(/ /g, "_");
				formData.append("audio_file", data.file, newFilename);
        		formData.append("flow_id", ivrId);
				payload = formData;
			}
			else if(data.payloadField) {
				payload = {
					[data.payloadField]: null
				};
			}
			else {
				payload = {
					flow_id: ivrId,
					...data,
				};
			} 
			const url = `/api/v1/core/ivr-component/${apiId}/`;
			await JService.patch(url, payload);
			// success message silenced
		}
		catch(error) {
			enqueueSnackbar(error.message, { variant: "error" });
			//TODO: on failure revert
		}
		finally {
			setShowLoader(false);
		}
	}
	// del: ivr-component/:id/
	async function deleteNodeAPI(id) {
		try {
			setShowClickAwayAlert(true);
			setShowLoader(true);
			const url = `/api/v1/core/ivr-component/${id}/`;
			console.log("delete:", id);
			await JService.delete(url);
			// success message silenced
		}
		catch(error) {
			enqueueSnackbar(error.message, { variant: "error" });
			//TODO: on failure revert
		}
		finally {
			setShowLoader(false);
		}
	}


	function onDelete(e, id, parentNodeId) {
		e.stopPropagation();
		deleteRef.current = {
			id,
			parentNodeId
		}
		setIsDeleteModalOpen(true);
	}

	function getChildNodes (id) {
		console.log("getChildNodes for", id);
		const res = [];
		let start = id;
		
		if(!connectToRef.current[start] || !connectToRef.current[start].next) return res;
		const action = (connectToRef.current[start]??{}).action;//(connectToRef.current[connectToRef.current[start].previous]??{}).selected; // check why nodesRef already removes nodes.
		if(action === NodeActions.ivr) {
			const node = nodesRef.current.find(node => node.id === id);
			const keypressActionNodeIds = node.data.keypressActions.map(kp => kp.id).filter(i => i);
			res.push(...keypressActionNodeIds);
			keypressActionNodeIds.map(kp => res.push(...getChildNodes(kp)));
		}
		else if (action === NodeActions.playAudio || connectToRef.current[start].action === NodeActions.playAudio) {
			const del = connectToRef.current[start].next;
			res.push(del);
			const children = getChildNodes(del);
			res.push(...children);
		}
		console.log("getChildNodes children for", id, res);
		return res;
	}

	// id and parentNodeId like localId
	function deleteNode(id, parentNodeId) {
		console.log("deleteNode:", id, "parent:", parentNodeId);
		const length = nodesRef.current.length;

		flushSync(() => {				
			selectNode(undefined);
			setSelectedNode(prev => ({
				...prev,
				id: undefined,
			}));
		});
		
		const children = getChildNodes(id, true);
		console.log("deleteNode: all children to be deleted", children);
		const op = nodesRef.current.filter(node => children.includes(node.id)).map(node => node.apiId);
		console.log("devlog: payload for bulk-del", [...op, (nodesMap[id]??{}).apiId]);
		
		const isParentIvr = (connectToRef.current[parentNodeId]??{}).action === NodeActions.ivr;
		const newConnectToState = {...connectToRef.current, [parentNodeId]: {...connectToRef.current[parentNodeId], next: undefined, selected: undefined}};
		
		let newNodes = nodesRef.current;
		if(isParentIvr) {
			newNodes = newNodes.map(node => node.id === parentNodeId ? ({
				...node,
				data: {
					...node.data,
					keypressActions: node.data.keypressActions.filter(kp => kp.id !== id),
				}
			}) : node);
		}
		const toDelete = nodesRef.current.filter(n => n.id === id || children.includes(n.id)).filter(n => n.apiId);
		toDelete.forEach(n => deleteNodeAPI(n.apiId));
		children.map(id => {
			delete newConnectToState[id];
			return id;
		});
		newNodes = newNodes.filter(node => node.id !== id && !children.includes(node.id));
		delete newConnectToState[id];
		setNodes(newNodes);
		setEdges(prev => prev.filter(edge => (edge.target !== id || edge.source !== parentNodeId) && !children.includes(edge.target) && !children.includes(edge.source)));
		setNodesMap(prev => {
			if(isParentIvr) {
				prev[parentNodeId] = {
					...prev[parentNodeId],
					data: {
						...prev[parentNodeId].data,
						keypressActions: prev[parentNodeId].data.keypressActions.filter(kp => kp.id !== id)
					}
				};
			}

			prev[parentNodeId] = {
				...prev[parentNodeId],
				data: {
					...(prev[parentNodeId] ?? {}).data,
					selected: undefined
				}
			};

			if(prev[id]) {
				delete prev[id];
			}

			children.forEach(child => {
				if(prev[child]) delete prev[child];
			});
			
			return prev;
		});
		setConnectToDropdown(newConnectToState);
		setIvrKeypressActions(prev => {
			if(isParentIvr) {
				prev[parentNodeId] = {
					...prev[parentNodeId],
					keypressActions: prev[parentNodeId].keypressActions.filter(kp => kp.id !== id),
				}
			}
			if(prev[id]) {
				delete prev[id];
			}
			return prev;
		});
		checkNodesValidity();
		return length - 1;
	}
	const deleteRef = useRef({});
	function handleConfirmNodeDelete() {
		setIsDeleteModalOpen(false);
		console.log("confirmed delete for", selectedNode, deleteRef.current);
		const {id, parentNodeId} = deleteRef.current;
		if(!id || !parentNodeId) {
			enqueueSnackbar("Could not delete node", {variant: "error"});
			return;
		}
		deleteNode(id, parentNodeId);
	}
	function handleCloseDeleteModal() {
		setIsDeleteModalOpen(false);
		deleteRef.current = {};
	}
	// clicking 'x' icon of keypress action in rightCol
	const handleRemoveKeyPressAction = (kp, ivrNodeId) => {
		setIvrKeypressActions(prev => ({
			...prev,
			[ivrNodeId]: {
				...prev[ivrNodeId],
				keypressActions: prev[ivrNodeId].keypressActions.filter(k => k.digit !== kp.digit),
			}
		}));
		setNodes(prev => prev.map(node => node.id === ivrNodeId ? ({
			...node,
			data: {
				...node.data,
				keypressActions: node.data.keypressActions.filter(k => k.digit !== kp.digit),
			}
		}) : node));
		
		if(kp.action) {
			setShowClickAwayAlert(true);
			deleteNode(kp.id);
		}
		checkNodesValidity();
	}


	// helpers to control position & selected state of ReactFlow nodes
	const handleRFNodeClick = (_, node) => selectNode(node.id);
	// select node by id
	function selectNode (id) {
		setNodes(prev => prev.map(node => node.id === id ? {...node, selected: true} : {...node, selected: false}));
	}
	// helper function to show selected node's data in rightCol and select the node in react-flow ui
	const highlightNode = (id, type, {keypressAction, digit}) => {
		let action;
		if(type === NodeTypes.ivrMenuNode) action = NodeActions.ivr;
		else if(type === NodeTypes.hangupNode) action = NodeActions.hangup;
		else if(keypressAction === NodeActions.toUser) action = NodeActions.toUser;
		else if(keypressAction === NodeActions.toTeam) action = NodeActions.toTeam;
		else if(type === NodeTypes.playAudioActionNode || keypressAction === NodeActions.playAudio) action = NodeActions.playAudio;
		else if(type === NodeTypes.incomingCallTriggerNode) action = NodeActions.trigger;

		setSelectedNode({id, action, isKeypressAction: keypressAction});
		selectNode(id);
	}
	// fired when 'change' detected by react-flow | change.type 'select'
	const handleNodesChange = useCallback((changes) => {
		// position updates
		onNodesChange(changes);
		
		const selectedNodesFromChanges = changes.find((node) => node.selected);
		if(!selectedNodesFromChanges) return;
		const selectedNode = nodes.find(node => node.id === selectedNodesFromChanges.id);
		const keypressAction = selectedNode?.data ? selectedNode.data.digit === "trigger" ? "" : selectedNode.data.action : "";
		const digit = selectedNode.data.digit ?? "";
		// FIXME: replace param sent as 'type' to 'action'
		highlightNode(selectedNode.id, selectedNode.type, {keypressAction, digit});

	}, [nodes, onNodesChange, setNodes]);



	// general ivr settings and modal handlers
	const handleSaveSettings = async (isDelete=false) => {
		try {
			const payload = {
				title: flowSettings.flowName,
				response_time: parseInt(flowSettings.maxResponseTime, 10),
				// invalidResponse
				repeat_question_option:
					flowSettings.invalidResponse === "Repeat audio 1 time"
					? 1
					: flowSettings.invalidResponse === "Repeat audio 2 times"
					? 2
					: 3,
				// noValidInput
				end_action:
					flowSettings.noValidInput === "End call with message"
					? 1
					: flowSettings.noValidInput === "Hang up immediately"
					? 2
					: 3, //Transfer to agent
			};
			if(isDelete) payload.end_action_recording_url = "";
			else if(flowSettings.endCallAudio) payload.audio_file = flowSettings.endCallAudio;
			
			const formData = new FormData();
			Object.entries(payload).forEach(([key, value]) => {
				if(key === "audio_file") {
					const newFilename = value.name.replace(/ /g, "_");
					formData.append(key, value, newFilename);
				}
				else formData.append(key, value);
			});
			const response = await JService.patch(`/api/v1/core/ivr-flow/${ivrId}/`, formData);
			if (!response?.success) throw new Error(response?.message);
			enqueueSnackbar("Settings saved successfully!", { variant: "success" });
			if(!isDelete) setIsSettingsModalOpen(false);
		}
		catch(error) {
			enqueueSnackbar(error.message, { variant: "error" });
		}
	}
	const handleSetFlowSettingsFile = (id, file) => {
		setFlowSettings(prev => ({
			...prev,
			endCallAudio: file,
		}));
	}
	const handleSetFlowSettingsAudioUrl = (id, url) => {
		setFlowSettings(prev => ({
			...prev,
			endCallAudioURL: url,
		}));
	}
	const handleDeleteFlowSettingsAudio = () => {
		setFlowSettings(prev => ({
			...prev,
			endCallAudioURL: '',
			endCallAudio: undefined,
			noValidInput: "Hang up immediately" // 'end call with message' needs a message, which is being deleted.
		}));
		handleSaveSettings(true);
	}
	function isFlowValid() {
		const startComponent = nodesRef.current.find(node => node.id === connectToDropdown[1].next);
		if(!startComponent || !startComponent.data) return false;
		const isStartComponentValid = startComponent.data.invalid === false;
		const nodesValid = nodesRef.current.every(node => (node.data ?? {}).invalid === false) && nodesRef.current.length > 1;
		return (isStartComponentValid && nodesValid);
	}

	const updateNodeValidity = (id, invalid) => {
		setNodes(prev => prev.map(node => node.id === id ? ({...node, data: {...node.data, invalid, showInvalid: showClickAwayAlert ? invalid : false}}) : node))
	}
	function isNodeInvalid(id) {
		let isInvalid;
		if(id === "1") return
		const node = nodesRef.current.find(node => node.id === id) ?? {};
		
		const action = (node.data??{}).action;
		const type = node.type;
		if(action === NodeActions.toUser) {
			const {selected} = connectToRef.current[id] ?? {};
			isInvalid = !selected;
		}
		else if(action === NodeActions.toTeam) {
			const {callDistribution, routingOrder, selected} = connectToRef.current[id] ?? {};
			isInvalid = !callDistribution || !Array.isArray(routingOrder) || !(routingOrder??"").length || !selected;
		}
		else if(action === NodeActions.playAudio) {
			const {selected} = connectToRef.current[id] ?? {};
			const audioObj = node.data.audio;//(data??{}).audio;
			isInvalid = !audioObj || !audioObj.file || !audioObj.audioUrl || !selected;
		}
		else if(type === NodeTypes.ivrMenuNode) {
			
			const keypressActions = node.data.keypressActions;
			const audioObj = node.data.audio;

			if(Array.isArray(keypressActions) && keypressActions.length) {
				const isUnassignedKpa = keypressActions.some(kp => [NodeActions.toUser, NodeActions.toTeam].includes(kp.action) ? !kp.selected : !kp.action);
				const isInvalidAudio = !audioObj || !audioObj.audioUrl || !audioObj.file; //TODO: audioObj.file instanceof File
				isInvalid = isUnassignedKpa || isInvalidAudio;
			}
			else isInvalid = true;
		}
		else if(type === NodeTypes.hangupNode) {
			isInvalid = false;
		}
		
		if(node && node.data) {
			// scheduling flushSync
			setTimeout(() => flushSync(() => {
				updateNodeValidity(id, isInvalid);
			}), 0);
		}
		return isInvalid;
	}

	function checkNodesValidity() {
		const sortedNodes = nodesRef.current.map(node => node.id);
		// check child before parent
		sortedNodes.sort((a, b) => Number(b) - Number(a));
		setValidateNodes(sortedNodes);
	}

	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if(!showClickAwayAlert) return;

			e.preventDefault();
			e.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		// window.addEventListener("navigate", handleNavigate); // does not fire on react router navigation
		return () => window.removeEventListener("beforeunload", handleBeforeUnload)
	}, [showClickAwayAlert]);

	const [isSettingsValid, setIsSettingsValid] = useState(true);
	const getBorderColorAfterValidtion = (value, hasNoBorder=false) => (showNodeFieldInvalidity && !value) ? localStyles.invalidBorderColor : hasNoBorder ? "rgba(255,255,255,0)" : localStyles.validBorderColor;
	return (
		<div className={styles.page}>
			<GHeader
				breadcrumbs={{
					isVisible: true,
					breadcrumbs,
					pointerEventsAllowed: true,
					displaySeparator: false
				}}
				bottomHeader={bottomHeader}
			/>

			{/* main body */}
			<div className={styles.container}>
				{/* react flow */}
				<div className={styles.column} ref={leftColumnRef}>
					<div style={{ height: "100vh", backgroundColor: "#FAFAFA" }}>
						<ReactFlow 
							nodes={nodes}
							minZoom={0.3}
              				maxZoom={2}
              				defaultViewport={{ x: 150, y: 50, zoom: 1 }}
              				nodeTypes={nodeTypes}
							elementsSelectable={true}
							nodesDraggable={true}
							
							edges={edges}
							onEdgesChange={onEdgesChange}
							connectionMode={ConnectionMode.Loose}
							
							// selection of nodes
							onNodeClick={handleRFNodeClick}
							onNodesChange={handleNodesChange}

							// Ctrl + scroll to zoom
							panOnScroll={true}
							selectionOnDrag={true}
							nodesConnectable={false} // disallow edge creation in ui mouse.

						>
							<Controls showZoom={true} position={"bottom-left"} showInteractive={false} showFitView={true}/>
							<Background />
						</ReactFlow>
					</div>
				</div>

				{/* right 'aside'-like element*/}
				{showRightColumn && (
					<div className={styles.RightColumn} style={localStyles.rightColumn.style}>
						<div style={localStyles.rightColumn.container["0"]}>
							<div className={`${styles.header} t7 nc-gray-900 medium-font`} >
								<span style={{fontSize: 16}}>{headerTitle ?? ""}</span>
							</div>
							<IconButton onClick={handleHideRightCloumn} style={localStyles.rightColumn.container.closeIcon}>
								<img src={CloseIcon} alt="Close" style={{ width: "12px", height: "12px" }} />
							</IconButton>
						</div>

						{/* body of right col */}
						<div style={{ flexGrow: 1, overflowY: "auto", padding: "24px", paddingBottom: "96px" }}>
							{selectedNode.id ? (
								<>
									{(selectedNode.action === NodeActions.trigger) && (
										<div>
											<CustomInput
												label="Component name"
												type="text"
												disabled
												value={{ value: "Trigger" }}
												placeholder="Enter title"
												style={{
													borderRadius: "6px",
													marginTop: "4px",
												}}
												labelStyles={{
													marginBottom: "unset"
												}}
											/>
											<hr style={{borderColor: "#E4E4E7", marginBlock: "24px", marginTop: "28px"}}/>
											<div className="t7 nc-gray-900 medium-font" style={{ marginBottom: "8px" }}>Then</div>
											<CustomSearchableDropdown
												width="360px"
												label="Select"
												placeholder="Search"
												options={connectToDropdown[selectedNode.id].options}
												selectedId={connectToDropdown[selectedNode.id].selected}
												onSelect={handleConnectToTrigger}
												disableSelectForSelected={true}
												renderContainerStyleOverride={{
													borderRadius: "6px",
												}}
											/>
										</div>
									)}
		
									{/* ivr selected */}
									{(selectedNode.action === NodeActions.ivr) && (
										<>
											<CustomInput
												label="Component name"
												type="text"
												value={{ value: nodesMap[selectedNode.id].data.name}}
												handleChange={e => handleUpdateComponentName(selectedNode.id, e.target.value)}
												placeholder="Enter title"
												style={{
													borderRadius: "6px",
													marginTop: "4px",
												}}
												labelStyles={{
													marginBottom: "unset"
												}}
											/>
											<hr style={{borderColor: "#E4E4E7", marginBlock: "24px", marginTop: "28px"}}/>
											<div className={`${styles.header} t7 nc-gray-900 medium-font`} style={{marginBottom: "8px"}}>Then</div>
											<div className={styles.quesDescContainer} style={{padding: 0, paddingBottom: "4px"}}>
												<div className={styles.keypressHeader} style={{backgroundColor: "var(--grey-50)", padding: "16px", margin: 0, borderRadius: "inherit"}}>
													<div className="t7 nc-gray-900 medium-font" style={{width: "72px"}}>Keypress</div>
													<div className="t7 nc-gray-900 medium-font">Action</div>
												</div>
												<hr style={{margin: 0, marginBottom: "16px"}}/>
												{ivrKeypressActions[selectedNode.id].keypressActions?.map(kp => (
													kp.isRendered ? (
														<div
															key={`right-keypress-${kp.digit}`}
															className={`${styles.keyContainer} t7 nc-gray-900 medium-font`}
															style={{ display: "flex", alignItems: "flex-start", padding: 0, paddingInline: "16px", paddingBottom: "12px", margin: 0  }}
														>
															<div className={styles.digit} style={{borderColor: localStyles.validBorderColor}}>
																<div className="t7 nc-gray-900 medium-font">{kp.digit}</div>
															</div>
			
															{/* keypress actions */}
															<div>
																{kp.action ? (
																	<div style={{ display: "flex", alignItems: "center" }}>
																		<div
																			style={{
																			width: "38px",
																			height: "38px",
																			borderRadius: "50%",
																			backgroundColor: "#F8E9FE",
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "center",
																			marginRight: "8px"
																			}}
																		>
																			<img src={getIcon(kp.action)} alt={kp.action} style={{ width: "20px", height: "20px" }} />
																		</div>
																		{kp.action === NodeActions.hangup || kp.action === NodeActions.playAudio || kp.action === NodeActions.ivr ? (
																			<CustomSearchableDropdown
																				width="150px"
																				paperWidth="200px"
																				label={kp.action} // "Selected"
																				placeholder="Search"
																				options={kp.options}
																				selectedId={kp.selected}
																				onSelect={(value) => handleUpdateNode({value, digit: kp.digit, action: kp.action, parentNodeId: selectedNode.id})} 
																				align="end"
																				autoOpen
																				disabled={true}
																				disableSelectForSelected={true}
																				renderContainerStyleOverride={{
																					borderRadius: "6px",
																				}}
																			/>
																			) : (
																			<CustomSearchableDropdown
																				width="150px"
																				paperWidth="200px"
																				label="Select"
																				placeholder="Search"
																				options={kp.options}
																				selectedId={kp.selected}
																				onSelect={(value) => handleUpdateNode({value, digit: kp.digit, action: kp.action, parentNodeId: selectedNode.id})} 
																				align="end"
																				autoOpen
																				disabled={false}
																				disableSelectForSelected={true}
																				renderContainerStyleOverride={{
																					borderRadius: "6px",
																					borderColor: getBorderColorAfterValidtion(kp.selected)
																				}}
																			/>
																			)
																		}
																	</div>
																) : (
																	// !kp.action case
																	<CustomSearchableDropdown
																		width="200px"
																		label="Select component"
																		placeholder="Search"
																		options={KeypressActionDropdownOptions.map((a) => ({ id: a, name: a }))}
																		selectedId={kp.action}
																		onSelect={(value) => handleSelectKeypressActionInDropdown(selectedNode.id, value, kp.digit)}
																		searchable={false}
																		autoOpen
																		disableSelectForSelected={true}
																		renderContainerStyleOverride={{
																			borderRadius: "6px",
																			borderColor: getBorderColorAfterValidtion(kp.action),
																		}}
																	/>
																)}
															</div>
															
															<IconButton onClick={() => handleRemoveKeyPressAction(kp, selectedNode.id)} size="small" className={styles.iconButton} style={{ marginRight: "8px", marginTop: "8px" }}>
																<img src={CloseIcon} alt="Close" style={{ width: "12px", height: "12px" }} />
															</IconButton>
														</div>
													) : null
												))}
											</div>
											{/* add new keypress action for '3', '4', ... */}
											<div className={styles.addButtonContainer} style={{marginTop: "10px", borderRadius: "6px", display: "flex", flexDirection: "column", alignItems: "stretch"}}>
												<Button
													variant="outlined"
													buttonText="Add Keypress"
													icon={{ startIcon: plusIcon() }}
													onClick={() => handleAddNewKeypressAction(selectedNode.id)}
													className={styles.addButton}
													styleOverride={{
														flex: 1,
														alignSelf: "stretch",
														"& .MuiButton-root": {
															width: "100%",
															margin: "0 !important",
															// add disabled style
														}
													}}
												/>
											</div>
											<div style={{ marginTop: "24px" }}>
												<Audio
													label="IVR audio message"
													file={ivrKeypressActions[selectedNode.id].audio.file}
													setFile={(_, file) => handleUpdateIvrAudio(selectedNode.id, {file})}
													setAudioURL={(_, audioUrl) => handleUpdateIvrAudio(selectedNode.id, {audioUrl})}
													id="audio"
													audioURL={ivrKeypressActions[selectedNode.id].audio.audioUrl}
													handleDeleteAudio={() => handleUpdateIvrAudio(selectedNode.id)}
													buttonStyle={{
														borderRadius: "6px",
														borderColor: getBorderColorAfterValidtion(ivrKeypressActions[selectedNode.id].audio.file)
													}}
													audioContainerStyle={{
														justifyContent: "unset",
														gap: "2px",
													}}
												/>
											</div>
										</>
									)}
		
									{/* non-kpa audio selected */}
									{selectedNode.action === NodeActions.playAudio && !selectedNode.isKeypressAction && (
										<>
											<div>
												<CustomInput
													label="Component name"
													type="text"
													value={{ value: nodesMap[selectedNode.id].data.name }}
													handleChange={(e) => handleUpdateComponentName(selectedNode.id, e.target.value)}
													placeholder="Enter title"
													style={{
														borderRadius: "6px",
														marginTop: "4px",
													}}
													labelStyles={{
													marginBottom: "unset"
												}}
												/>
												<hr style={{borderColor: "#E4E4E7", marginBlock: "24px", marginTop: "28px"}}/>
												<div>
													<Audio
														label="Play audio message"
														file={nodesMap[selectedNode.id].data.audio.file}
														setFile={(_, file) => handleUpdateAudioNode(selectedNode.id, {file})}
														setAudioURL={(_, audioUrl) => handleUpdateAudioNode(selectedNode.id, {audioUrl})}
														id={`playAudio-${"trigger"}`}
														audioURL={nodesMap[selectedNode.id].data.audio.audioUrl}
														handleDeleteAudio={() => handleUpdateAudioNode(selectedNode.id)}
														audioContainerStyle={{
															justifyContent: "unset",
															gap: "2px",
														}}
														buttonStyle={{
															borderRadius: "6px",
															marginTop: "8px",
															borderColor: getBorderColorAfterValidtion(nodesMap[selectedNode.id].data.audio.file)
														}}
													/>
												</div>
												<div className="t7 nc-gray-900 medium-font" style={{ marginBottom: "8px", marginTop: "24px" }}>Then</div>
												<CustomSearchableDropdown
													width="360px"
													label="Connect to"
													placeholder="Select"
													options={connectToDropdown[selectedNode.id].options}
													selectedId={connectToDropdown[selectedNode.id].selected}
													onSelect={(value) => handleConnectToForAudioNode(selectedNode.id, value, selectedNode.isKeypressAction)}
													autoOpen
													disableSelectForSelected={true}
													renderContainerStyleOverride={{
														borderRadius: "6px",
														borderColor: getBorderColorAfterValidtion(connectToDropdown[selectedNode.id].selected)
													}}
												/>
											</div>
										</>
									)}

									{/* hangup selected */}
									{selectedNode.action === NodeActions.hangup && (
										<CustomInput
											label="Component name"
											type="text"
											value={{ value: "Hang up node" }}
											handleChange={(e) => console.log("update hangup node name", e.target.value)}
											placeholder="Enter title"
											disabled
											style={{
												borderRadius: "6px",
												marginTop: "4px",
											}}
										/>
									)}

									{/* kpa selected but neither hangup or ivr*/}
									{selectedNode.isKeypressAction && selectedNode.isKeypressAction !== NodeActions.ivr && selectedNode.action !== NodeActions.hangup && (
										<div>
											<CustomInput
												label="Component name"
												type="text"
												value={{ value: nodesMap[selectedNode.id].data.name}}
												handleChange={(e) => handleUpdateComponentName(selectedNode.id, e.target.value)}
												placeholder="Enter title"
												style={{
													borderRadius: "6px",
													marginTop: "4px",
												}}
												labelStyles={{
													marginBottom: "unset"
												}}
											/>
											<hr style={{borderColor: "#E4E4E7", marginBlock: "24px", marginTop: "28px"}}/>
											<div style={{ marginTop: "24px" }}>
												{/* below for toUser and toTeam */}
												{[NodeActions.toTeam, NodeActions.toUser].includes(selectedNode.action) && (
													<>
														<div className="t7 nc-gray-900 medium-font" style={{ marginBottom: "8px" }}>Then</div>
														<CustomSearchableDropdown
															width="360px"
															label={`Select ${nodesMap[selectedNode.id].data.label}`}
															placeholder="Search" // ${nodesMap[selectedNode.id].data.label}s`}
															options={connectToDropdown[selectedNode.id].options ?? []}
															selectedId={connectToDropdown[selectedNode.id].selected}
															onSelect={(value) => handleUpdateNode({value, digit: nodesMap[selectedNode.id].data.digit, updatedNodeId: selectedNode.id, action: connectToDropdown[selectedNode.id].action})} // type like Connect to User
															autoOpen
															disableSelectForSelected={true}
															renderContainerStyleOverride={{
																borderRadius: "6px",
																borderColor: getBorderColorAfterValidtion(connectToDropdown[selectedNode.id].selected)
															}}
														/>
														{/* call distribution */}
														{selectedNode.action === NodeActions.toTeam && (
															<div style={{marginTop: "24px"}}>
																<div className="t7 nc-gray-900 medium-font" style={{ marginTop: "24px", marginBottom: "8px" }}>Call distribution</div>
																<CustomSearchableDropdown
																	width="360px"
																	label="Select Distribution"
																	placeholder="Search"
																	options={connectToDropdown[selectedNode.id].callDistributionOptions}
																	selectedId={connectToDropdown[selectedNode.id].callDistribution}
																	onSelect={(value) => handleUpdateCallDistribution({value, digit: nodesMap[selectedNode.id].data.digit, updatedNodeId: selectedNode.id, isCallDistribution: true})}
																	autoOpen
																	disableSelectForSelected={true}
																	renderContainerStyleOverride={{
																		borderRadius: "6px",
																		borderColor: getBorderColorAfterValidtion(connectToDropdown[selectedNode.id].callDistribution)
																	}}
																/>
															</div>
														)}
													</>
												)}
												{/* below for playAudio */}
												{selectedNode.isKeypressAction === NodeActions.playAudio && (
													<>
														<div style={{ marginTop: "24px" }}>
														<Audio
															label="Play audio message"
															file={nodesMap[selectedNode.id].data.audio.file}
															setFile={(_, file) => handleUpdateAudioNode(selectedNode.id, {file})}
															setAudioURL={(_, audioUrl) => handleUpdateAudioNode(selectedNode.id, {audioUrl})}
															id={`playAudio-${nodesMap[selectedNode.id].data.digit}`}
															audioURL={nodesMap[selectedNode.id].data.audio.audioUrl}
															handleDeleteAudio={() => handleUpdateAudioNode(selectedNode.id)}
															buttonStyle={{
																borderRadius: "6px",
																borderColor: getBorderColorAfterValidtion(nodesMap[selectedNode.id].data.audio.file)
															}}
														/>
														</div>
														<div className="t7 nc-gray-900 medium-font" style={{ marginBottom: "8px", marginTop: "24px" }}>Then</div>
														<CustomSearchableDropdown
															width="360px"
															label="Connect to"
															placeholder="Select"
															options={connectToDropdown[selectedNode.id].options}
															selectedId={connectToDropdown[selectedNode.id].selected}
															onSelect={(value) => handleConnectToForAudioNode(selectedNode.id, value, true)}
															autoOpen
															disableSelectForSelected={true}
															renderContainerStyleOverride={{
																borderRadius: "6px",
																borderColor: getBorderColorAfterValidtion(connectToDropdown[selectedNode.id].selected)
															}}
														/>
													</>
												)}
												{selectedNode.action === NodeActions.toTeam && connectToDropdown[selectedNode.id].callDistribution == 2
												&& (
													<Button
														variant={"outlined"}
														buttonText="Edit order"
														buttonStyle={{
															float: "right",
															color: "#28994b",
															fontSize: "14px",
															background: "white",
															paddingRight: "8px",
															fontWeight: "500",

															position: "absolute",
															right: 0,
															margin: 0,
															padding: 0,
															marginRight: "16px",
															background: "white !important",
														}}
														icon={{}}
														onClick={() => setIsEditOrderModalOpen(true)}
														// disabled if not Fixed order or team not selected
														disabled={connectToDropdown[selectedNode.id].callDistribution != 2 || !connectToDropdown[selectedNode.id].selected}
													/>
												)}
											</div>
										</div>
									)}
								</>
							) : null}
						</div>
					</div>
				)}
			</div>

			{showLoader ? createPortal(<div style={{top: 0, left: 0, position: "fixed", width: "100vw", height: "100vh", zIndex: 29999, display: "flex", background: "rgba(255, 255, 255, 0.5)", justifyContent: "center", alignItems: "center"}}>
				<AnimationPage style={{display: "flex", alignItems: "center", justifyContent: "center"}}/>
			</div>, document.body) : null}
			
			{!!selectedNode.id && 
				<GModal	
					closeModal={handleCloseDeleteModal}
					heading={<div className="t6 nc-gray-900 medium-font">Delete component</div>}
					visible={isDeleteModalOpen}
					bottomButtons={[
						<Button
							key="cancel"
							variant="contained"
							buttonText="Cancel"
							hierarchy="white"
							onClick={() => handleCloseDeleteModal()}
							styleOverride={{
								border: "1px solid var(--grey-200) !important",
							}}
						/>,
						<Button
							key="confirm"
							variant="contained"
							buttonText="Delete"
							hierarchy="red"
							onClick={handleConfirmNodeDelete}
						/>
					]}
					body={
						<div className="t7 nc-gray-900 regular-font">
							<p style={{lineHeight: "22px", color: "#3F3F46"}}>
									Are you sure you want to delete
								<span className="medium-font">
									&nbsp;{nodesMap[selectedNode.id].data.name}
								</span>
								{String(nodesMap[selectedNode.id].data.name).toLowerCase().includes("component") ? "" : "component"}?
								<br/>
								This will also delete all connected components that come
								<br/>
								after it.
							</p>
						</div>	
					}
				/>
			}
			{flowSettings && <GModal
				closeModal={() => setIsSettingsModalOpen(false)}
				body={<SettingsModalBody
					flowNamesInUse={flowNamesInUse} 
					flowSettings={flowSettings}
					maxResponseOptions={maxResponseOptions} 
					invalidResponseOptions={invalidResponseOptions}
					noValidInputOptions={noValidInputOptions}
					setFlowSettings={setFlowSettings}
					handleSetFlowSettingsFile={handleSetFlowSettingsFile}
					handleSetFlowSettingsAudioUrl={handleSetFlowSettingsAudioUrl}
					handleDeleteFlowSettingsAudio={handleDeleteFlowSettingsAudio}
					setIsSettingsValid={setIsSettingsValid}
					/>}
				heading={<div className="t6 nc-gray-900 medium-font">Flow settings</div>}
        		visible={isSettingsModalOpen}
				bottomButtons={[
					<Button
						key="cancel"
						variant="contained"
						buttonText="Cancel"
						hierarchy="white"
						onClick={() => setIsSettingsModalOpen(false)}
						styleOverride={{
							border: "1px solid var(--grey-200) !important",
						}}
					/>,
					<Button
						key="save"
						variant="contained"
						buttonText="Save"
						hierarchy="green"
						onClick={() => handleSaveSettings()}
						disabled={!isSettingsValid}
					/>
				]}
			/>}
			{isEditOrderModalOpen && selectedNode.id && connectToDropdown[selectedNode.id].action === NodeActions.toTeam && (
				<EditOrderModal
					vnId={virtualNumberId}
					originalInboundTeam={connectToDropdown[selectedNode.id].selected}
					connectToTeam={connectToDropdown[selectedNode.id].selected}
					allTeams={teams}
					allUsers={users}
					routingOrder={connectToDropdown[selectedNode.id].routingOrder}
					onClose={() => setIsEditOrderModalOpen(false)}
					handleUpdate={(routingOrder) => handleUpdateCallDistribution({value: routingOrder, digit: nodesMap[selectedNode.id].data.digit, updatedNodeId: selectedNode.id, isCallDistribution: false})}
					callDistribution={connectToDropdown[selectedNode.id].callDistribution}
				/>
			)}
		</div>
	)
}

function SettingsModalBody ({setIsSettingsValid, flowSettings, maxResponseOptions, invalidResponseOptions, noValidInputOptions, setFlowSettings, handleSetFlowSettingsFile, handleSetFlowSettingsAudioUrl, handleDeleteFlowSettingsAudio, flowNamesInUse}) {
	const {flowName, maxResponseTime, invalidResponse, noValidInput, endCallAudio, endCallAudioURL} = flowSettings;
	useMemo(() => {
		const isValid = !!(flowName && flowName.length) && !!maxResponseTime && !!invalidResponse && !!noValidInput && noValidInput === "End call with message" ? (!!endCallAudio || !!endCallAudioURL) : !!noValidInput;
		setIsSettingsValid(isValid && !flowNamesInUse.includes(flowName.toLowerCase()));
		return isValid;
	}, [flowSettings]);
	return (
		<div >
			<CustomInput
				
				inputProps={{title: flowNamesInUse.includes(flowName.toLowerCase()) ? "Choose an unique name for ivr" : ""}}
				label="Flow name"
				type="text"
				value={{ value: flowSettings.flowName }}
				handleChange={(e) => setFlowSettings((p) => ({ ...p, flowName: e.target.value }))}
				placeholder="Flow name"
				style={{
					borderRadius: "6px",
					marginTop: "0px",
				}}
				labelStyles={{
					marginBottom: "20px",
				}}
			/>
			<hr style={{borderColor: "#E4E4E7", marginBottom: "16px"}}/>
			{/* maxResponse time */}
			<div style={{ marginTop: "0px" }}>
				<CustomSelect
					label="Max response time"
					value={flowSettings.maxResponseTime}
					handleChange={(e) => {
						setFlowSettings((p) => ({ ...p, maxResponseTime: e.target.value }))
					}}
					options={formatFlowSettingsOptions(maxResponseOptions)}
					defaultValue="Select"
					selectStyles={{ width: "100%", marginTop: "4px" }}
				/>
			</div>
			{/* invalidResponse */}
			<div style={{ marginTop: "20px" }}>
				<CustomSelect
					label="If invalid response"
					value={flowSettings.invalidResponse}
					handleChange={(e) => setFlowSettings((p) => ({ ...p, invalidResponse: e.target.value }))}
					options={formatFlowSettingsOptions(invalidResponseOptions)}
					defaultValue="Select"
					selectStyles={{
						width: "100%",
						marginTop: "4px"
					}}
				/>
			</div>
			{/* no valid response */}
			<div style={{ marginTop: "20px" }}>
				<CustomSelect
					label="If no valid input after repeats"
					value={flowSettings.noValidInput}
					handleChange={(e) => setFlowSettings((p) => ({ ...p, noValidInput: e.target.value }))}
					options={formatFlowSettingsOptions(noValidInputOptions)}
					defaultValue="Select"
					selectStyles={{
						width: "100%",
						marginTop: "4px"
					}}					
				/>
			</div>
			{flowSettings.noValidInput === "End call with message" && (
				<Audio
					label="End call with message"
					file={flowSettings.endCallAudio}
					setFile={handleSetFlowSettingsFile}
					setAudioURL={handleSetFlowSettingsAudioUrl}
					id="flowSettingsAudio"
					audioURL={flowSettings.endCallAudioURL}
					handleDeleteAudio={handleDeleteFlowSettingsAudio}
					buttonStyle={{
						borderRadius: "6px",
					}}
					labelStyle={{
						marginTop: "0px",
						display: "inline-block",
						width: "100%",
						color: "white",
						userSelect: "none",
						fontSize: 0,
					}}
				/>
      		)}
		</div>
	)
}

function formatFlowSettingsOptions(options) {
  return options.map((option) => ({ value: option, displayValue: option }));
}
function getType(type) {
	if(type === NodeActions.ivr) return "ivr_node";
	else if (type === NodeActions.playAudio) return "recording";
	else if (type === NodeActions.toTeam) return "team";
	else if (type === NodeActions.toUser) return "user";
	else if (type === NodeActions.hangup) return "hangup";
	return ""
}
const getIcon = (action) => {
	if(action === NodeActions.toUser) return UserIcon
	else if(action === NodeActions.toTeam) return UsersIcon
	else if(action === NodeActions.ivr) return Dialer
	else if(action === NodeActions.playAudio) return PlayAudio
	else if(action === NodeActions.hangup) return HangupIcon
}
const localStyles = {
	rightColumn: {
		style: {position: "relative", display: "flex", flexDirection: "column", height: "100vh"},
		container: {
			"0": {
                position: "relative",
                padding: "16px",
                backgroundColor:'#FAFAFA',
                borderBottom: "1px solid #E0E0E0"
            },
			closeIcon: {
				position: "absolute",
				right: "16px",
				top: "50%",
				transform: "translateY(-50%)",
				border: "1px solid #E4E0E7",
				borderRadius: "4px",
				padding: "8px"
			}
		}
	},
	invalidBorderColor: "#E22D20",
	validBorderColor: "#e4e4e7",
}